import React, { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../utils/api";
import Cargando from "../components/Cargando";
import { Ayuda } from "../context/AyudaContext";
import { descargarReporteExcel } from "../utils/reporteExcel";

const MAXIMO_POR_LISTA = 8;
const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const fechaIso = (fecha) =>
  `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, "0")}-${String(fecha.getDate()).padStart(2, "0")}`;

const hoy = () => new Date();
const inicioAnio = () => new Date(hoy().getFullYear(), 0, 1);
const inicioMes = () => new Date(hoy().getFullYear(), hoy().getMonth(), 1);

const cordobas = (n) =>
  `C$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const cordobasCorto = (n) => {
  const valor = Number(n || 0);
  if (valor >= 1000000) return `C$${(valor / 1000000).toFixed(1)} M`;
  if (valor >= 1000) return `C$${Math.round(valor / 1000)} mil`;
  return `C$${Math.round(valor)}`;
};

// Menos de un día se muestra en horas para no aparecer como "0".
const formatoTiempo = (dias) => {
  if (dias === null || dias === undefined) return "—";
  if (dias < 1) {
    const horas = Math.max(1, Math.round(dias * 24));
    return `${horas} ${horas === 1 ? "hora" : "horas"}`;
  }
  return `${dias.toFixed(1)} días`;
};

const nombreMes = (clave) => {
  const [anio, mes] = clave.split("-");
  return `${MESES[Number(mes) - 1]} ${anio.slice(2)}`;
};

function BarrasHorizontales({ datos }) {
  const visibles = datos.slice(0, MAXIMO_POR_LISTA);
  const resto = datos.slice(MAXIMO_POR_LISTA);
  const lista = resto.length
    ? [...visibles, { nombre: `Otros (${resto.length})`, monto: resto.reduce((suma, d) => suma + d.monto, 0) }]
    : visibles;
  const maximo = Math.max(...lista.map((d) => d.monto), 1);
  return (
    <div style={estilos.lista}>
      {lista.map((d) => (
        <div key={d.nombre} className="reporte-fila" style={estilos.fila} title={`${d.nombre}: ${cordobas(d.monto)}`}>
          <span style={estilos.filaNombre}>{d.nombre}</span>
          <span style={estilos.filaValor}>{cordobas(d.monto)}</span>
          <div style={estilos.pista}>
            <div style={{ ...estilos.barra, width: `${Math.max((d.monto / maximo) * 100, 1)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ColumnasPorMes({ datos }) {
  const maximo = Math.max(...datos.map((d) => d.monto), 1);
  return (
    <div style={estilos.columnas}>
      {datos.map((d) => (
        <div key={d.nombre} className="reporte-columna" style={estilos.columna} title={`${nombreMes(d.nombre)}: ${cordobas(d.monto)}`}>
          <span style={estilos.columnaValor}>{cordobasCorto(d.monto)}</span>
          <div style={estilos.columnaPista}>
            <div style={{ ...estilos.columnaBarra, height: `${Math.max((d.monto / maximo) * 100, 2)}%` }} />
          </div>
          <span style={estilos.columnaMes}>{nombreMes(d.nombre)}</span>
        </div>
      ))}
    </div>
  );
}

function Grafica({ titulo, datos, children }) {
  return (
    <section style={estilos.tarjeta}>
      <div className="form-seccion-titulo">{titulo}</div>
      {datos.length === 0 ? <p style={estilos.sinDatos}>Sin datos en este período.</p> : children}
    </section>
  );
}

function Reportes() {
  const [desde, setDesde] = useState(() => fechaIso(inicioAnio()));
  const [hasta, setHasta] = useState(() => fechaIso(hoy()));
  const [areaId, setAreaId] = useState("");
  const [areas, setAreas] = useState([]);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const consulta = new URLSearchParams({ desde, hasta, ...(areaId ? { area_id: areaId } : {}) });
      setDatos(await apiFetch(`/api/reportes?${consulta}`));
    } catch (err) {
      setError(err.message || "No se pudo generar el reporte.");
    } finally {
      setCargando(false);
    }
  }, [desde, hasta, areaId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    apiFetch("/api/areas")
      .then((lista) => setAreas(Array.isArray(lista) ? lista : []))
      .catch(() => setAreas([]));
  }, []);

  const elegirPeriodo = (inicio) => {
    setDesde(fechaIso(inicio));
    setHasta(fechaIso(hoy()));
  };

  const resumen = datos?.resumen;

  return (
    <div>
      <h2>📈 Reportes</h2>
      <Ayuda>
        <p style={{ color: "#5b6472", marginTop: "0.25rem" }}>
          El gasto suma lo aprobado con IVA, en córdobas, según la fecha de aprobación. Las solicitudes se cuentan por su fecha de creación.
        </p>
      </Ayuda>

      <div className="barra-filtros" style={estilos.filtros}>
        <button type="button" style={estilos.botonPeriodo} onClick={() => elegirPeriodo(inicioMes())}>
          Este mes
        </button>
        <button type="button" style={estilos.botonPeriodo} onClick={() => elegirPeriodo(inicioAnio())}>
          Este año
        </button>
        <label style={estilos.campoFecha}>
          Desde
          <input type="date" value={desde} max={hasta} onChange={(e) => setDesde(e.target.value)} style={estilos.input} />
        </label>
        <label style={estilos.campoFecha}>
          Hasta
          <input type="date" value={hasta} min={desde} onChange={(e) => setHasta(e.target.value)} style={estilos.input} />
        </label>
        <select aria-label="Área" value={areaId} onChange={(e) => setAreaId(e.target.value)} style={estilos.input}>
          <option value="">Todas las áreas</option>
          {areas.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nombre}
            </option>
          ))}
        </select>
        <button
          type="button"
          style={{
            ...estilos.botonExportar,
            ...(!datos || datos.detalle.length === 0 ? { opacity: 0.45, cursor: "not-allowed" } : {}),
          }}
          disabled={!datos || datos.detalle.length === 0}
          onClick={() =>
            descargarReporteExcel({
              datos,
              desde,
              hasta,
              area: areas.find((a) => String(a.id) === String(areaId))?.nombre || "Todas las áreas",
              tiempo: formatoTiempo(datos.resumen.dias_promedio),
            })
          }
        >
          ⬇️ Exportar a Excel
        </button>
      </div>

      {error && <p style={{ color: "#a3402f" }}>{error}</p>}

      {cargando && !datos ? (
        <Cargando compacto />
      ) : (
        datos && (
          <div style={{ opacity: cargando ? 0.6 : 1 }}>
            <div className="tarjetas-resumen" style={{ marginTop: "0.9rem" }}>
              <div style={estilos.resumen}>
                <span style={estilos.resumenTexto}>Gasto aprobado</span>
                <strong style={estilos.resumenValor}>{cordobas(resumen.gasto_nio)}</strong>
              </div>
              <div style={estilos.resumen}>
                <span style={estilos.resumenTexto}>Solicitudes creadas</span>
                <strong style={estilos.resumenValor}>{resumen.solicitudes}</strong>
              </div>
              <div style={estilos.resumen}>
                <span style={estilos.resumenTexto}>Rechazadas</span>
                <strong style={estilos.resumenValor}>{resumen.rechazadas}</strong>
              </div>
              <div style={estilos.resumen}>
                <span style={estilos.resumenTexto}>Tiempo promedio de aprobación</span>
                <strong style={estilos.resumenValor}>{formatoTiempo(resumen.dias_promedio)}</strong>
              </div>
            </div>

            <div style={{ marginTop: "0.9rem" }}>
              <Grafica titulo="Gasto por mes" datos={datos.por_mes}>
                <ColumnasPorMes datos={datos.por_mes} />
              </Grafica>
            </div>

            <div className="reportes-rejilla" style={estilos.rejilla}>
              <Grafica titulo="Gasto por área" datos={datos.por_area}>
                <BarrasHorizontales datos={datos.por_area} />
              </Grafica>
              <Grafica titulo="Gasto por proveedor" datos={datos.por_proveedor}>
                <BarrasHorizontales datos={datos.por_proveedor} />
              </Grafica>
              <Grafica titulo="Productos con más gasto" datos={datos.por_producto}>
                <BarrasHorizontales datos={datos.por_producto} />
              </Grafica>
              <Grafica titulo="Contado y crédito" datos={datos.por_condicion}>
                <BarrasHorizontales datos={datos.por_condicion} />
              </Grafica>
            </div>
          </div>
        )
      )}
    </div>
  );
}

const estilos = {
  filtros: { display: "flex", flexWrap: "wrap", marginTop: "0.9rem" },
  campoFecha: { display: "flex", fontSize: "0.8rem", color: "#5b6472" },
  input: { padding: "0.42rem 0.55rem", border: "1px solid #d3d9df", borderRadius: 6, fontSize: "0.85rem" },
  botonPeriodo: {
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    border: "1px solid #d3d9df",
    borderRadius: 999,
    padding: "0.38rem 0.8rem",
    fontSize: "0.8rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  botonExportar: {
    marginLeft: "auto",
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.5rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 700,
    cursor: "pointer",
  },
  resumen: {
    display: "flex",
    flexDirection: "column",
    gap: "0.2rem",
    background: "#fff",
    border: "1px solid #e2e6ea",
    padding: "0.8rem 1rem",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
  },
  resumenTexto: { fontSize: "0.74rem", fontWeight: 700, color: "#5b6472", textTransform: "uppercase", letterSpacing: "0.03em" },
  resumenValor: { fontSize: "1.45rem", color: "var(--tema-titulo, #0b3c60)", fontVariantNumeric: "tabular-nums" },
  tarjeta: {
    background: "#fff",
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    padding: "1rem 1.1rem",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
    minWidth: 0,
  },
  sinDatos: { margin: "0.4rem 0", fontSize: "0.85rem", color: "#5b6472" },
  rejilla: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "0.75rem",
    marginTop: "0.75rem",
  },
  lista: { display: "flex", flexDirection: "column", gap: "0.3rem" },
  fila: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    columnGap: "0.6rem",
    padding: "0.25rem 0.3rem",
    borderRadius: 6,
  },
  filaNombre: { fontSize: "0.82rem", color: "#1c2733", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  filaValor: { fontSize: "0.82rem", fontWeight: 600, color: "#1c2733", fontVariantNumeric: "tabular-nums" },
  pista: { gridColumn: "1 / -1", marginTop: "0.2rem" },
  barra: { height: 8, borderRadius: "0 4px 4px 0", background: "var(--tema-primario, #1f4e96)" },
  columnas: { display: "flex", gap: "0.5rem", overflowX: "auto", paddingBottom: "0.2rem" },
  columna: { flex: "1 0 58px", maxWidth: 110, display: "flex", flexDirection: "column", alignItems: "center", gap: "0.25rem", borderRadius: 6 },
  columnaValor: { fontSize: "0.72rem", fontWeight: 600, color: "#1c2733", whiteSpace: "nowrap" },
  columnaPista: { width: "100%", height: 150, display: "flex", alignItems: "flex-end", justifyContent: "center", borderBottom: "1px solid #d3d9df" },
  columnaBarra: { width: "60%", maxWidth: 34, borderRadius: "4px 4px 0 0", background: "var(--tema-primario, #1f4e96)" },
  columnaMes: { fontSize: "0.72rem", color: "#5b6472", whiteSpace: "nowrap" },
};

export default Reportes;
